/**
 * Spec 36, Fachregel 5 (ersetzt Spec 35, Fachregel 2): die Dokumentliste in
 * Gruppen gliedern.
 *
 * Reine Funktion über die bereits geordnete Liste des Servers. Der Server
 * liefert je Zeile `list_group` (`value` = Schlüssel, `label` = fertige
 * Überschrift: „Heute", „Di., 13. Okt." oder „Juli 2026"); der Client liest
 * kein Datum, kennt kein „heute" und bildet keinen Monatsnamen. Weil die
 * Eingabe die zusammengesetzte Liste aller bisher geladenen Seiten ist, setzt
 * eine nachgeladene Seite die letzte Gruppe von selbst fort (keine doppelte
 * Überschrift).
 */

export interface DocumentListGroup<T> {
  /** `list_group.value`; `null` ohne Gliederung (Suche). */
  readonly key: string | null
  /** `list_group.label` wörtlich; `null` ohne Gliederung. */
  readonly heading: string | null
  readonly items: readonly T[]
}

/** Tagesschlüssel `JJJJ-MM-TT` haben zehn Zeichen, Monatsschlüssel `JJJJ-MM`
 * sieben — eine Formfrage, keine Fachzeit (Spec 36, Fachregel 5). */
const DAY_KEY_LENGTH = 10

/** `document-day-<JJJJ-MM-TT>` bzw. `document-month-<JJJJ-MM>`; in beiden
 * Clients gleich. */
export function groupTestId(key: string): string {
  return `${key.length === DAY_KEY_LENGTH ? 'document-day' : 'document-month'}-${key}`
}

/**
 * Mit Suchbegriff (Relevanzsortierung, Spec 24a) eine einzige Gruppe ohne
 * Überschrift; sonst aufeinanderfolgende Zeilen mit gleichem
 * `list_group.value` in Server-Reihenfolge.
 */
export function groupDocuments<T extends { readonly list_group: { readonly value: string; readonly label: string } }>(
  documents: readonly T[],
  isSearching: boolean,
): DocumentListGroup<T>[] {
  if (documents.length === 0) return []
  if (isSearching) return [{ key: null, heading: null, items: documents }]

  const groups: { key: string; heading: string; items: T[] }[] = []
  for (const document of documents) {
    const { value, label } = document.list_group
    const last = groups.at(-1)
    if (last?.key === value) last.items.push(document)
    else groups.push({ key: value, heading: label, items: [document] })
  }
  return groups
}
